import {
  DEFAULT_ESTABLISHMENT,
  DEFAULT_HOSPITAL_REVENUE_SETTINGS,
  DEFAULT_PHARMACY_SETTINGS,
  DEFAULT_RECEPTION_SETTINGS,
  type EstablishmentProfile,
  type EstablishmentSite,
  type EstablishmentSiteInput,
  type HospitalRevenueSettings,
  type MedicalUnit,
  type MedicalUnitInput,
  type PharmacySettings,
  type ReceptionSettings,
  type RoleMenuMappingRow,
} from "./types";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { shouldAutoSeedDemoData } from "@/core/demo/is-demo-mode-enabled";
import { syncTenantProfileFromSession } from "@itaas/core/auth/sync-tenant-profile";
import type { AuthSession } from "@itaas/core/auth/models/session.model";
import {
  apiGetAdminSettings,
  apiPutAdminSettings,
  apiListMedicalUnits,
  apiSyncMedicalUnit,
  apiDeleteMedicalUnit,
  apiListEstablishmentSites,
  apiSyncEstablishmentSite,
  apiDeleteEstablishmentSite,
} from "@/core/hospitol-api-client";

const PROFILE_KEY = "hospitol.establishment.profile";
const PHARMACY_KEY = "hospitol.establishment.pharmacy";
const RECEPTION_KEY = "hospitol.establishment.reception";
const HOSPITAL_REVENUE_KEY = "hospitol.establishment.hospitalRevenue";
const UNITS_KEY = "hospitol.medicalUnits";
const SITES_KEY = "hospitol.establishmentSites";
const DEMO_SEED_KEY = "hospitol.admin.demoSeeded";
const SETTINGS_EVENT = "hospitol-settings-changed";

function persistSettings(key: string, value: unknown): void {
  writeJson(key, value);
  window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
}

export function subscribeSettingsChange(onChange: () => void): () => void {
  const handler = () => onChange();
  window.addEventListener(SETTINGS_EVENT, handler);
  return () => window.removeEventListener(SETTINGS_EVENT, handler);
}

export function getEstablishmentProfile(): EstablishmentProfile {
  return { ...DEFAULT_ESTABLISHMENT, ...readJson<Partial<EstablishmentProfile>>(PROFILE_KEY, {}) };
}

export function isDefaultEstablishmentProfile(profile: EstablishmentProfile): boolean {
  const name = profile.name.trim();
  return !name || name === DEFAULT_ESTABLISHMENT.name || name === "Centre hospitalier démo ITaaS";
}

/** Aligne le profil local sur le nom IAM si encore au défaut démo (post-provision / handoff). */
export function ensureEstablishmentProfileFromSession(
  session: AuthSession | null | undefined,
): void {
  syncTenantProfileFromSession(session, {
    getProfile: getEstablishmentProfile,
    saveProfile: saveEstablishmentProfile,
    defaultProfile: DEFAULT_ESTABLISHMENT,
    isDefaultProfile: isDefaultEstablishmentProfile,
  });
}

export function saveEstablishmentProfile(profile: EstablishmentProfile): void {
  persistSettings(PROFILE_KEY, profile);
  void apiPutAdminSettings({ profile });
}

export function getPharmacySettings(): PharmacySettings {
  return { ...DEFAULT_PHARMACY_SETTINGS, ...readJson<Partial<PharmacySettings>>(PHARMACY_KEY, {}) };
}

export function savePharmacySettings(settings: PharmacySettings): void {
  const normalized = {
    currency: settings.currency,
    invoicePrefix: settings.invoicePrefix.trim().toUpperCase() || DEFAULT_PHARMACY_SETTINGS.invoicePrefix,
    requirePatientOnInvoice: settings.requirePatientOnInvoice,
    requirePharmacistValidation: settings.requirePharmacistValidation,
    syncBillingOnInvoice: settings.syncBillingOnInvoice,
    allowPharmacistDirectPrescription: settings.allowPharmacistDirectPrescription,
  };
  persistSettings(PHARMACY_KEY, normalized);
  void apiPutAdminSettings({ pharmacySettings: normalized });
}

export function getReceptionSettings(): ReceptionSettings {
  return {
    ...DEFAULT_RECEPTION_SETTINGS,
    ...readJson<Partial<ReceptionSettings>>(RECEPTION_KEY, {}),
  };
}

export function saveReceptionSettings(settings: ReceptionSettings): void {
  const normalized = {
    allowReceptionConsultationProforma: settings.allowReceptionConsultationProforma,
    generalConsultationFee: Math.max(0, settings.generalConsultationFee),
  };
  persistSettings(RECEPTION_KEY, normalized);
  void apiPutAdminSettings({ receptionSettings: normalized });
}

export function getHospitalRevenueSettings(): HospitalRevenueSettings {
  return {
    ...DEFAULT_HOSPITAL_REVENUE_SETTINGS,
    ...readJson<Partial<HospitalRevenueSettings>>(HOSPITAL_REVENUE_KEY, {}),
  };
}

export function saveHospitalRevenueSettings(settings: HospitalRevenueSettings): void {
  const normalized = {
    emergencyVisitFee: Math.max(0, settings.emergencyVisitFee),
    hospitalizationDailyFee: Math.max(0, settings.hospitalizationDailyFee),
    nursingCareFee: Math.max(0, settings.nursingCareFee),
    surgeryProcedureFee: Math.max(0, settings.surgeryProcedureFee),
    ambulanceTransportFee: Math.max(0, settings.ambulanceTransportFee),
  };
  persistSettings(HOSPITAL_REVENUE_KEY, normalized);
  void apiPutAdminSettings({ hospitalRevenueSettings: normalized });
}

/** Pull durable settings from API; push local if remote empty defaults. */
export async function syncEstablishmentSettingsFromApi(): Promise<boolean> {
  const remote = await apiGetAdminSettings();
  if (remote === null) return false;
  const hasRemoteName = Boolean(remote.profile?.name?.trim());
  if (hasRemoteName || remote.updatedBy) {
    persistSettings(PROFILE_KEY, remote.profile);
    persistSettings(PHARMACY_KEY, remote.pharmacySettings);
    persistSettings(RECEPTION_KEY, remote.receptionSettings);
    persistSettings(HOSPITAL_REVENUE_KEY, remote.hospitalRevenueSettings);
    return true;
  }
  const local = {
    profile: getEstablishmentProfile(),
    pharmacySettings: getPharmacySettings(),
    receptionSettings: getReceptionSettings(),
    hospitalRevenueSettings: getHospitalRevenueSettings(),
  };
  if (local.profile.name.trim() || !isDefaultEstablishmentProfile(local.profile)) {
    void apiPutAdminSettings(local);
  }
  return true;
}

export function ensureDemoMedicalUnits(): void {
  if (!shouldAutoSeedDemoData()) return;
  if (getStorageItem(DEMO_SEED_KEY)) return;

  const demos: MedicalUnitInput[] = [
    { code: "MG", name: "Médecine générale", boxes: ["Box 1", "Box 2", "Box 3"] },
    { code: "URG", name: "Urgences", boxes: ["Box urgences"] },
    { code: "PED", name: "Pédiatrie", boxes: ["Box pédiatrie"] },
    { code: "MAT", name: "Maternité", boxes: ["Box maternité"] },
    { code: "CHIR", name: "Chirurgie / bloc", boxes: ["Bloc 1", "Bloc 2"] },
    { code: "LAB", name: "Laboratoire", boxes: ["Prélèvement 1", "Prélèvement 2"] },
    { code: "IMG", name: "Imagerie", boxes: ["Salle A", "Salle B"] },
    { code: "PHAR", name: "Pharmacie", boxes: ["Guichet 1", "Guichet 2"] },
  ];

  for (const input of demos) {
    createMedicalUnit(input);
  }

  setStorageItem(DEMO_SEED_KEY, new Date().toISOString());
}

export function listMedicalUnits(): MedicalUnit[] {
  ensureDemoMedicalUnits();
  return readJson<MedicalUnit[]>(UNITS_KEY, []).sort((a, b) => a.name.localeCompare(b.name, "fr"));
}

export function listActiveMedicalUnitNames(): string[] {
  return listMedicalUnits()
    .filter((u) => u.active)
    .map((u) => u.name);
}

export function hasSurgeryMedicalUnit(): boolean {
  return listMedicalUnits().some((u) => u.active && /chirurg/i.test(u.name));
}

/** Ajoute l'unité bloc si absente (mise en route / orientation chirurgie). */
export function ensureSurgeryMedicalUnit(): MedicalUnit {
  const existing = listMedicalUnits().find((u) => u.active && /chirurg/i.test(u.name));
  if (existing) return existing;
  return createMedicalUnit({
    code: "CHIR",
    name: "Chirurgie / bloc",
    boxes: ["Bloc 1", "Bloc 2"],
  });
}

export function createMedicalUnit(input: MedicalUnitInput): MedicalUnit {
  const unit: MedicalUnit = {
    id: `unit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    code: input.code.trim().toUpperCase(),
    name: input.name.trim(),
    active: input.active ?? true,
    siteId: input.siteId ?? null,
    boxes: input.boxes,
  };
  const list = readJson<MedicalUnit[]>(UNITS_KEY, []);
  list.push(unit);
  writeJson(UNITS_KEY, list);
  window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
  void apiSyncMedicalUnit(unit);
  return unit;
}

export function updateMedicalUnit(id: string, patch: Partial<MedicalUnitInput>): MedicalUnit | null {
  const list = readJson<MedicalUnit[]>(UNITS_KEY, []);
  const index = list.findIndex((u) => u.id === id);
  if (index < 0) return null;

  const updated: MedicalUnit = {
    ...list[index]!,
    code: patch.code?.trim().toUpperCase() ?? list[index]!.code,
    name: patch.name?.trim() ?? list[index]!.name,
    active: patch.active ?? list[index]!.active,
    siteId: patch.siteId !== undefined ? patch.siteId : list[index]!.siteId,
    boxes: patch.boxes ?? list[index]!.boxes,
  };
  list[index] = updated;
  writeJson(UNITS_KEY, list);
  window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
  void apiSyncMedicalUnit(updated);
  return updated;
}

export function deleteMedicalUnit(id: string): boolean {
  const list = readJson<MedicalUnit[]>(UNITS_KEY, []);
  const next = list.filter((u) => u.id !== id);
  if (next.length === list.length) return false;
  writeJson(UNITS_KEY, next);
  window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
  void apiDeleteMedicalUnit(id);
  return true;
}

/** Pull durable medical units; push local if remote empty. */
export async function syncMedicalUnitsFromApi(): Promise<boolean> {
  const remote = await apiListMedicalUnits();
  if (remote === null) return false;
  if (remote.length > 0) {
    writeJson(
      UNITS_KEY,
      remote.map((u) => ({
        id: u.id,
        code: u.code,
        name: u.name,
        active: u.active,
        siteId: u.siteId ?? null,
        boxes: u.boxes,
      })),
    );
    window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
    return true;
  }
  for (const u of readJson<MedicalUnit[]>(UNITS_KEY, [])) {
    void apiSyncMedicalUnit(u);
  }
  return true;
}

export function listEstablishmentSites(): EstablishmentSite[] {
  return readJson<EstablishmentSite[]>(SITES_KEY, []);
}

export function createEstablishmentSite(input: EstablishmentSiteInput): EstablishmentSite {
  const site: EstablishmentSite = {
    id: `site-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    code: input.code.trim().toUpperCase(),
    name: input.name.trim(),
    active: input.active ?? true,
  };
  const list = readJson<EstablishmentSite[]>(SITES_KEY, []);
  list.push(site);
  writeJson(SITES_KEY, list);
  window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
  void apiSyncEstablishmentSite(site);
  return site;
}

export function updateEstablishmentSite(
  id: string,
  patch: Partial<EstablishmentSiteInput>,
): EstablishmentSite | null {
  const list = readJson<EstablishmentSite[]>(SITES_KEY, []);
  const index = list.findIndex((s) => s.id === id);
  if (index < 0) return null;
  const updated: EstablishmentSite = {
    ...list[index]!,
    code: patch.code?.trim().toUpperCase() ?? list[index]!.code,
    name: patch.name?.trim() ?? list[index]!.name,
    active: patch.active ?? list[index]!.active,
  };
  list[index] = updated;
  writeJson(SITES_KEY, list);
  window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
  void apiSyncEstablishmentSite(updated);
  return updated;
}

export function deleteEstablishmentSite(id: string): boolean {
  const list = readJson<EstablishmentSite[]>(SITES_KEY, []);
  const next = list.filter((s) => s.id !== id);
  if (next.length === list.length) return false;
  writeJson(SITES_KEY, next);
  window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
  void apiDeleteEstablishmentSite(id);
  return true;
}

/** Pull durable sites; push local if remote empty. */
export async function syncEstablishmentSitesFromApi(): Promise<boolean> {
  const remote = await apiListEstablishmentSites();
  if (remote === null) return false;
  if (remote.length > 0) {
    writeJson(
      SITES_KEY,
      remote.map((s) => ({
        id: s.id,
        code: s.code,
        name: s.name,
        active: s.active,
      })),
    );
    window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
    return true;
  }
  for (const s of readJson<EstablishmentSite[]>(SITES_KEY, [])) {
    void apiSyncEstablishmentSite(s);
  }
  return true;
}

/** Matrice IAM → sections menu (documentation admin). */
export const ROLE_MENU_MATRIX: RoleMenuMappingRow[] = [
  {
    permissionKey: "hospitol.access",
    label: "Accès module",
    menuSections: ["Tout Hospitol"],
  },
  {
    permissionKey: "hospitol.dashboard.read",
    label: "Tableau de bord",
    menuSections: ["Accueil"],
  },
  {
    permissionKey: "hospitol.admissions.read",
    label: "Accueil & flux",
    menuSections: ["Admissions", "Urgences"],
  },
  {
    permissionKey: "hospitol.patients.read",
    label: "Patients",
    menuSections: ["Liste patients"],
  },
  {
    permissionKey: "hospitol.clinical.read",
    label: "Clinique",
    menuSections: ["Consultations", "Prescriptions"],
  },
  {
    permissionKey: "hospitol.pharmacy.read",
    label: "Pharmacie",
    menuSections: ["Facture", "Stock", "Historique"],
  },
  {
    permissionKey: "hospitol.laboratory.read",
    label: "Laboratoire",
    menuSections: ["Analyses", "Résultats"],
  },
  {
    permissionKey: "hospitol.imaging.read",
    label: "Imagerie",
    menuSections: ["Examens", "Comptes-rendus"],
  },
  {
    permissionKey: "hospitol.nursing.read",
    label: "Soins infirmiers",
    menuSections: ["Constantes & tournées"],
  },
  {
    permissionKey: "hospitol.nursing.vitals.write",
    label: "Saisie constantes infirmier",
    menuSections: ["Constantes & tournées (écriture)"],
  },
  {
    permissionKey: "hospitol.hospitalization.read",
    label: "Hospitalisation",
    menuSections: ["Lits & séjours"],
  },
  {
    permissionKey: "hospitol.beds.assign",
    label: "Attribution lits",
    menuSections: ["Lits & séjours (attribution)"],
  },
  {
    permissionKey: "hospitol.billing.read",
    label: "Facturation",
    menuSections: ["Recettes & factures"],
  },
  {
    permissionKey: "hospitol.rh.read",
    label: "RH",
    menuSections: ["Employés", "Départements", "Plannings"],
  },
  {
    permissionKey: "hospitol.stock.read",
    label: "Stock hospitalier",
    menuSections: ["Approvisionnement"],
  },
  {
    permissionKey: "hospitol.insurance.read",
    label: "Assurance médicale",
    menuSections: ["Tableau de bord assurance", "Compagnies", "Prises en charge", "Partenariats hub"],
  },
  {
    permissionKey: "hospitol.admin.read",
    label: "Administration",
    menuSections: ["Paramètres", "Utilisateurs", "Menus démo", "Assurance (direction)"],
  },
];

export function previewNextInvoiceNumber(): string {
  const { invoicePrefix } = getPharmacySettings();
  const seq = Number(localStorage.getItem("pharmacy.invoiceSeq") ?? "0") + 1;
  return `${invoicePrefix}-${String(seq).padStart(5, "0")}`;
}
